import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Button,
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useAppContext, useFeature } from '../../../../Context/AppContext';
import { useRealtimeEvent } from '../../../../Context/RealtimeContext';
import {
	deleteJobAssignment,
	fetchJobAssignments,
} from '../../../../Shared/Api';
import { surfaceCard } from '../../../../Shared/surfaceCard';
import SectionHeader from './SectionHeader';
import AssignmentModal from '../../../Schedule/AssignmentModal';

const formatRange = (startsAt, endsAt) => {
	const start = dayjs(startsAt);
	const end = dayjs(endsAt);
	if (!start.isValid() || !end.isValid()) return '—';
	if (start.isSame(end, 'day')) {
		return `${start.format('ddd MMM D, h:mm A')} – ${end.format('h:mm A')}`;
	}
	return `${start.format('MMM D, h:mm A')} – ${end.format('MMM D, h:mm A')}`;
};

const JobSchedulePanel = ({ job, onChanged }) => {
	const theme = useTheme();
	const { state } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const hasScheduling = useFeature('scheduling');
	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const [assignments, setAssignments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);

	const load = useCallback(async () => {
		if (!hasScheduling || !job?.id) return;
		setLoading(true);
		try {
			// Always use the filtered assignments API (employees see own only).
			const data = await fetchJobAssignments({ job: job.id });
			setAssignments(data || []);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to load job schedule.', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	}, [enqueueSnackbar, hasScheduling, job?.id]);

	useEffect(() => {
		load();
	}, [load]);

	const handleRealtime = useCallback(
		(message) => {
			if (
				message?.type === 'job.assignment_updated' &&
				String(message.job_id) === String(job?.id)
			) {
				load();
			}
		},
		[job?.id, load],
	);
	useRealtimeEvent(handleRealtime);

	if (!hasScheduling) {
		return null;
	}

	const handleDelete = async (assignment) => {
		try {
			await deleteJobAssignment(assignment.id);
			enqueueSnackbar('Assignment deleted.', { variant: 'success' });
			load();
			onChanged?.();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to delete assignment.', {
				variant: 'error',
			});
		}
	};

	return (
		<Box sx={surfaceCard(theme)}>
			<SectionHeader
				icon={CalendarMonthIcon}
				title="Schedule"
				action={
					<Stack direction="row" spacing={1} alignItems="center">
						<Button
							size="small"
							component={RouterLink}
							to="/schedule"
							sx={{ textTransform: 'none' }}
						>
							View on schedule
						</Button>
						{isManager ? (
							<Button
								size="small"
								startIcon={<AddIcon />}
								onClick={() => {
									setEditing(null);
									setModalOpen(true);
								}}
							>
								Add
							</Button>
						) : null}
					</Stack>
				}
			/>
			<Divider sx={{ opacity: 0.6 }} />
			<Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
				{loading && !assignments.length ? (
					<Typography variant="body2" color="text.secondary">
						Loading...
					</Typography>
				) : !assignments.length ? (
					<Typography variant="body2" color="text.secondary">
						No assignments on this job yet.
					</Typography>
				) : (
					<Stack spacing={1.5}>
						{assignments.map((assignment) => (
							<Box
								key={assignment.id}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									gap: 1,
									alignItems: 'flex-start',
								}}
							>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" fontWeight={600}>
										{assignment.user_name}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{formatRange(assignment.starts_at, assignment.ends_at)}
									</Typography>
									{assignment.notes ? (
										<Typography variant="caption" color="text.secondary">
											{assignment.notes}
										</Typography>
									) : null}
								</Box>
								{isManager && (
									<Stack direction="row" spacing={0.25}>
										<IconButton
											size="small"
											aria-label="Edit assignment"
											onClick={() => {
												setEditing(assignment);
												setModalOpen(true);
											}}
										>
											<EditOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											aria-label="Delete assignment"
											onClick={() => handleDelete(assignment)}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</Stack>
								)}
							</Box>
						))}
					</Stack>
				)}
			</Box>

			{isManager && (
				<AssignmentModal
					open={modalOpen}
					onClose={() => {
						setModalOpen(false);
						setEditing(null);
					}}
					assignment={editing}
					fixedJob={{
						id: job.id,
						name: job.name,
						customer_name: job.customer_name,
					}}
					onSaved={() => {
						load();
						onChanged?.();
					}}
				/>
			)}
		</Box>
	);
};

export default JobSchedulePanel;
